import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Star, Check, ChevronDown, ChevronUp, ArrowRight, Users, Clock,
  Sparkles, Shield, Award, Zap, Heart, Calendar, Phone, Mail,
  MapPin, Gift, Play, Target
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = {
  star: Star, check: Check, users: Users, clock: Clock,
  sparkles: Sparkles, shield: Shield, award: Award, zap: Zap,
  heart: Heart, calendar: Calendar, phone: Phone, mail: Mail,
  mappin: MapPin, gift: Gift, play: Play, target: Target
};

const COLOR_SCHEMES = {
  indigo: { primary: '#6366f1', gradient: 'from-indigo-600 to-purple-700', accent: 'text-indigo-600', bg: 'bg-indigo-50 dark:bg-indigo-950/30', border: 'border-indigo-200', btn: 'bg-indigo-600 hover:bg-indigo-700' },
  emerald: { primary: '#059669', gradient: 'from-emerald-600 to-teal-700', accent: 'text-emerald-600', bg: 'bg-emerald-50 dark:bg-emerald-950/30', border: 'border-emerald-200', btn: 'bg-emerald-600 hover:bg-emerald-700' },
  rose: { primary: '#e11d48', gradient: 'from-rose-600 to-pink-700', accent: 'text-rose-600', bg: 'bg-rose-50 dark:bg-rose-950/30', border: 'border-rose-200', btn: 'bg-rose-600 hover:bg-rose-700' },
  amber: { primary: '#d97706', gradient: 'from-amber-600 to-orange-700', accent: 'text-amber-600', bg: 'bg-amber-50 dark:bg-amber-950/30', border: 'border-amber-200', btn: 'bg-amber-600 hover:bg-amber-700' },
  violet: { primary: '#7c3aed', gradient: 'from-violet-600 to-purple-800', accent: 'text-violet-600', bg: 'bg-violet-50 dark:bg-violet-950/30', border: 'border-violet-200', btn: 'bg-violet-600 hover:bg-violet-700' }
};

function getIcon(name) {
  const Icon = ICON_MAP[name?.toLowerCase()] || Sparkles;
  return Icon;
}

export default function MarketingPageRenderer() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const previewPageId = searchParams.get('preview');
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [leadForm, setLeadForm] = useState({ name: '', email: '', phone: '', event_date: '', event_type: '', guest_count: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const visitorId = useRef(localStorage.getItem('mktg_visitor') || (() => { const id = 'v_' + Math.random().toString(36).slice(2, 10); localStorage.setItem('mktg_visitor', id); return id; })());

  const fetchPage = useCallback(async () => {
    try {
      let res;
      // If preview mode with page_id, use admin preview endpoint
      if (previewPageId) {
        const token = localStorage.getItem('eventism_token');
        if (token) {
          try {
            res = await axios.get(`${API}/marketing/page-preview/${previewPageId}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
          } catch {
            // Fallback to public endpoint if admin preview fails
            res = await axios.get(`${API}/marketing/page/${slug}`);
          }
        } else {
          res = await axios.get(`${API}/marketing/page/${slug}`);
        }
      } else {
        res = await axios.get(`${API}/marketing/page/${slug}`);
      }
      setPage(res.data);
      // Track view
      axios.post(`${API}/marketing/track`, {
        page_id: res.data.page_id,
        event_type: 'view',
        visitor_id: visitorId.current,
        utm_source: new URLSearchParams(window.location.search).get('utm_source') || '',
        utm_medium: new URLSearchParams(window.location.search).get('utm_medium') || '',
        utm_campaign: new URLSearchParams(window.location.search).get('utm_campaign') || ''
      }).catch(() => {});
    } catch {
      setPage(null);
    } finally {
      setLoading(false);
    }
  }, [slug, previewPageId]);

  useEffect(() => { fetchPage(); }, [fetchPage]);

  const trackClick = (action) => {
    if (!page) return;
    axios.post(`${API}/marketing/track`, {
      page_id: page.page_id,
      event_type: 'cta_click',
      visitor_id: visitorId.current
    }).catch(() => {});
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    if (!leadForm.email) { toast.error('Please enter your email'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/marketing/lead`, {
        page_id: page.page_id,
        ...leadForm,
        guest_count: parseInt(leadForm.guest_count) || 0,
        coupon_code: page.coupon_code || ''
      });
      setSubmitted(true);
      toast.success('Thank you! We\'ll be in touch soon.');
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-pulse text-muted-foreground">Loading...</div></div>;
  if (!page) return <div className="min-h-screen flex items-center justify-center"><div className="text-center"><h2 className="text-2xl font-bold mb-2">Page Not Found</h2><p className="text-muted-foreground">This page may have been removed.</p><Button className="mt-4" onClick={() => navigate('/')}>Go Home</Button></div></div>;

  const content = page.content || {};
  const colors = COLOR_SCHEMES[content.color_scheme] || COLOR_SCHEMES.indigo;
  const sections = content.sections || [];
  const images = content.service_images || [];
  const services = page.services || [];

  return (
    <div className={`min-h-screen bg-background ${page.standalone ? '' : 'pt-16'}`} data-testid="marketing-page">
      {/* Hero */}
      <section className={`relative bg-gradient-to-br ${colors.gradient} text-white overflow-hidden`}>
        <div className="absolute inset-0 bg-black/20" />
        {images[0] && <div className="absolute inset-0 opacity-15" style={{ backgroundImage: `url(${images[0]})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />}
        <div className="relative max-w-4xl mx-auto px-6 py-20 md:py-28 text-center">
          {content.urgency_text && (
            <div className="inline-block bg-white/20 backdrop-blur-sm rounded-full px-4 py-1.5 mb-6" data-testid="urgency-badge">
              <p className="text-sm font-semibold tracking-wide">{content.urgency_text}</p>
            </div>
          )}
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-4" data-testid="hero-headline">
            {content.headline || page.title}
          </h1>
          <p className="text-lg md:text-xl text-white/85 max-w-2xl mx-auto mb-8">
            {content.subheadline}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {page.cta_types?.includes('book_now') && (
              <Button size="lg" className="bg-white text-slate-900 hover:bg-white/90 text-lg px-8" onClick={() => { trackClick('book_now'); navigate('/plan-event'); }} data-testid="hero-cta-book">
                {content.cta_text || 'Get Your Free Quote'} <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            )}
            {page.cta_types?.includes('lead_form') && (
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/20 text-lg px-8" onClick={() => { trackClick('lead_form'); document.getElementById('lead-form')?.scrollIntoView({ behavior: 'smooth' }); }} data-testid="hero-cta-lead">
                {content.cta_secondary_text || 'Get Started'} <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            )}
            {!page.cta_types?.includes('book_now') && !page.cta_types?.includes('lead_form') && page.cta_types?.includes('coupon') && (
              <Button size="lg" className="bg-white text-slate-900 hover:bg-white/90 text-lg px-8" onClick={() => { trackClick('coupon'); document.getElementById('offer-section')?.scrollIntoView({ behavior: 'smooth' }); }} data-testid="hero-cta-coupon">
                Claim Your Offer <Gift className="ml-2 h-5 w-5" />
              </Button>
            )}
          </div>
        </div>
      </section>

      {/* Dynamic Sections */}
      {sections.map((section, idx) => (
        <SectionRenderer key={idx} section={section} colors={colors} images={images} services={services} idx={idx} />
      ))}

      {/* Testimonials */}
      {content.testimonials?.length > 0 && (
        <section className="py-16 bg-muted/30">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-10">What Our Clients Say</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {content.testimonials.map((t, i) => (
                <Card key={i} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex gap-0.5 mb-3">{Array(t.rating || 5).fill(0).map((_, j) => <Star key={j} className="h-4 w-4 fill-amber-400 text-amber-400" />)}</div>
                    <p className="text-sm text-muted-foreground italic mb-4">"{t.quote}"</p>
                    <div>
                      <p className="font-semibold text-sm">{t.name}</p>
                      <p className="text-xs text-muted-foreground">{t.role}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      {content.faq?.length > 0 && (
        <section className="py-16">
          <div className="max-w-2xl mx-auto px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-10">Frequently Asked Questions</h2>
            <div className="space-y-3">
              {content.faq.map((item, i) => (
                <div key={i} className="border rounded-lg overflow-hidden">
                  <button className="w-full flex items-center justify-between p-4 text-left font-medium hover:bg-muted/50 transition-colors" onClick={() => setExpandedFaq(expandedFaq === i ? null : i)}>
                    {item.question}
                    {expandedFaq === i ? <ChevronUp className="h-4 w-4 flex-shrink-0" /> : <ChevronDown className="h-4 w-4 flex-shrink-0" />}
                  </button>
                  {expandedFaq === i && (
                    <div className="px-4 pb-4 text-sm text-muted-foreground">{item.answer}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Lead Form */}
      {page.cta_types?.includes('lead_form') && (
        <section id="lead-form" className={`py-16 ${colors.bg}`}>
          <div className="max-w-xl mx-auto px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-2">Get Started Today</h2>
            <p className="text-center text-muted-foreground mb-8">Tell us about your event and we'll create a custom plan for you.</p>
            {submitted ? (
              <Card data-testid="lead-submitted">
                <CardContent className="p-8 text-center">
                  <Check className="h-12 w-12 text-emerald-500 mx-auto mb-4" />
                  <h3 className="text-xl font-bold mb-2">Thank You!</h3>
                  <p className="text-muted-foreground">We've received your info and will be in touch within 24 hours.</p>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-6">
                  <form onSubmit={handleLeadSubmit} className="space-y-4" data-testid="lead-capture-form">
                    <div className="grid grid-cols-2 gap-3">
                      <Input placeholder="Your Name" value={leadForm.name} onChange={e => setLeadForm(p => ({ ...p, name: e.target.value }))} data-testid="lead-name" />
                      <Input placeholder="Email *" type="email" required value={leadForm.email} onChange={e => setLeadForm(p => ({ ...p, email: e.target.value }))} data-testid="lead-email" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <Input placeholder="Phone" value={leadForm.phone} onChange={e => setLeadForm(p => ({ ...p, phone: e.target.value }))} data-testid="lead-phone" />
                      <Input placeholder="Event Date" type="date" value={leadForm.event_date} onChange={e => setLeadForm(p => ({ ...p, event_date: e.target.value }))} data-testid="lead-date" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <Input placeholder="Event Type" value={leadForm.event_type} onChange={e => setLeadForm(p => ({ ...p, event_type: e.target.value }))} data-testid="lead-event-type" />
                      <Input placeholder="Guest Count" type="number" value={leadForm.guest_count} onChange={e => setLeadForm(p => ({ ...p, guest_count: e.target.value }))} data-testid="lead-guests" />
                    </div>
                    <Button type="submit" className={`w-full ${colors.btn} text-white`} size="lg" disabled={submitting} data-testid="lead-submit-btn">
                      {submitting ? 'Submitting...' : (content.cta_text || 'Get My Free Quote')}
                    </Button>
                    <p className="text-xs text-muted-foreground text-center">No obligation. We'll respond within 24 hours.</p>
                  </form>
                </CardContent>
              </Card>
            )}
          </div>
        </section>
      )}

      {/* Coupon Section */}
      {page.cta_types?.includes('coupon') && page.coupon_code && (
        <section id="offer-section" className="py-16 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20">
          <div className="max-w-lg mx-auto px-6 text-center">
            <Gift className="h-12 w-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-2xl md:text-3xl font-bold mb-2">Special Offer</h2>
            <p className="text-muted-foreground mb-6">{page.offer || 'Use this exclusive code at checkout'}</p>
            <div className="inline-block border-2 border-dashed border-amber-500 rounded-xl px-8 py-4 bg-white dark:bg-card" data-testid="coupon-display">
              <p className="text-3xl font-mono font-bold tracking-widest text-amber-600">{page.coupon_code}</p>
            </div>
            <div className="mt-6">
              <Button size="lg" className="bg-amber-500 hover:bg-amber-600 text-white" onClick={() => { trackClick('coupon_use'); navigate('/plan-event'); }} data-testid="coupon-use-btn">
                Use Code &amp; Plan Your Event <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Book Now CTA */}
      {page.cta_types?.includes('book_now') && (
        <section className={`py-16 bg-gradient-to-br ${colors.gradient} text-white`}>
          <div className="max-w-2xl mx-auto px-6 text-center">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">{content.cta_text || 'Ready to Plan Your Event?'}</h2>
            <p className="text-white/80 mb-8 text-lg">Get an instant quote in under 2 minutes. No obligation.</p>
            <Button size="lg" className="bg-white text-slate-900 hover:bg-white/90 text-lg px-10" onClick={() => { trackClick('book_now_bottom'); navigate('/plan-event'); }} data-testid="bottom-cta-book">
              Get Your Free Quote <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </section>
      )}

      {/* Footer */}
      {page.standalone && (
        <footer className="py-8 bg-slate-900 text-white/60 text-center text-sm">
          <p>Eventism &middot; Denver, Colorado &middot; <a href="mailto:support@eventism.com" className="text-white/80 hover:text-white">support@eventism.com</a></p>
          <p className="mt-1">&copy; {new Date().getFullYear()} Eventism. All rights reserved.</p>
        </footer>
      )}
    </div>
  );
}

function SectionRenderer({ section, colors, images, services, idx }) {
  if (!section) return null;
  const items = section.items || [];

  switch (section.type) {
    case 'benefits':
      return (
        <section className="py-16">
          <div className="max-w-5xl mx-auto px-6">
            {section.headline && <h2 className="text-2xl md:text-3xl font-bold text-center mb-10">{section.headline}</h2>}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {items.map((item, i) => {
                const Icon = getIcon(item.icon);
                return (
                  <div key={i} className="text-center p-6">
                    <div className={`w-12 h-12 mx-auto mb-4 rounded-xl ${colors.bg} flex items-center justify-center`}>
                      <Icon className={`h-6 w-6 ${colors.accent}`} />
                    </div>
                    <h3 className="font-semibold mb-2">{item.title}</h3>
                    <p className="text-sm text-muted-foreground">{item.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      );

    case 'services':
      return (
        <section className={`py-16 ${colors.bg}`}>
          <div className="max-w-5xl mx-auto px-6">
            {section.headline && <h2 className="text-2xl md:text-3xl font-bold text-center mb-10">{section.headline}</h2>}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.length > 0 ? services.map((svc, i) => (
                <Card key={i} className="overflow-hidden hover:shadow-lg transition-shadow">
                  {(svc.gallery_images?.[0] || svc.image_url) && (
                    <img src={svc.gallery_images?.[0] || svc.image_url} alt={svc.name} className="w-full h-40 object-cover" />
                  )}
                  <CardContent className="p-4">
                    <h3 className="font-semibold">{svc.name}</h3>
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{svc.description}</p>
                    {svc.base_price > 0 && <p className={`text-sm font-semibold mt-2 ${colors.accent}`}>From ${svc.base_price}</p>}
                  </CardContent>
                </Card>
              )) : items.map((item, i) => (
                <Card key={i} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <h3 className="font-semibold mb-2">{item.title}</h3>
                    <p className="text-sm text-muted-foreground">{item.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      );

    case 'how_it_works':
      return (
        <section className="py-16">
          <div className="max-w-3xl mx-auto px-6">
            {section.headline && <h2 className="text-2xl md:text-3xl font-bold text-center mb-10">{section.headline}</h2>}
            <div className="space-y-8">
              {items.map((item, i) => (
                <div key={i} className="flex gap-4 items-start">
                  <div className={`w-10 h-10 rounded-full ${colors.btn} text-white flex items-center justify-center font-bold flex-shrink-0`}>
                    {i + 1}
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">{item.title}</h3>
                    <p className="text-sm text-muted-foreground">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      );

    case 'social_proof':
    case 'problem':
    case 'solution':
    case 'pricing':
    case 'offer':
    case 'urgency':
    case 'cta':
    default:
      return (
        <section className={`py-16 ${idx % 2 === 0 ? '' : 'bg-muted/30'}`}>
          <div className="max-w-3xl mx-auto px-6 text-center">
            {section.headline && <h2 className="text-2xl md:text-3xl font-bold mb-4">{section.headline}</h2>}
            {section.content && <p className="text-muted-foreground text-base md:text-lg leading-relaxed mb-6">{section.content}</p>}
            {items.length > 0 && (
              <div className="grid sm:grid-cols-2 gap-4 text-left mt-8">
                {items.map((item, i) => (
                  <div key={i} className="flex items-start gap-3 p-3">
                    <Check className={`h-5 w-5 ${colors.accent} flex-shrink-0 mt-0.5`} />
                    <div>
                      <p className="font-medium text-sm">{item.title}</p>
                      {item.description && <p className="text-xs text-muted-foreground">{item.description}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      );
  }
}
